import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Grid,
  IconButton,
  SwipeableDrawer,
  Typography,
  useTheme,
} from '@mui/material';

import ProvinceGraphs from './ProvinceGraphs';
import ProvinceView from './ProvinceView';
import { Province } from '../../../@types/KingdomResponse';
import { intelColors } from '../../../Constants';
import { useIntelState } from '../../../context/IntelState';
import { effects, getEffect, resolveSVG } from '../../../Effects';
import {
  findKanbanItemFromId,
  findKanbanItemFromProvinceId,
} from '../../../helpers/GeneralHelpers';
import { ReactComponent as OpsSVG } from '../../../svgs/design/nav-ops-svg-1.svg';
import ProvinceOps from '../../WarPlanner/ProvinceOps';
import {
  convertSpellNameToUpper,
  getSvgItemWithTooltip,
  renderSpell,
} from '../helpers/colHelpers';
const iOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

interface Props {
  row: Province;
  hideDetailPanel: () => void;
  isEnemy: boolean;
}

const DetailPanelContent: React.FC<Props> = ({
  row: province,
  hideDetailPanel,
  isEnemy,
}) => {
  const theme = useTheme();
  const { intelState, intelDispatch } = useIntelState();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [opsDrawerOpen, setOpsDrawerOpen] = useState(false);

  const green = intelColors[theme.palette.mode].freshest;
  const red = intelColors[theme.palette.mode].oldest;
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
    if (drawerOpen) hideDetailPanel();
  };

  const toggleOpsDraw = () => {
    setOpsDrawerOpen(!opsDrawerOpen);
  };

  const kanbanItem = isEnemy
    ? findKanbanItemFromProvinceId(intelState.enemyPlan, province.id)
    : null;

  const renderOp = (spell: string, isToDo: boolean) => {
    const effect = effects[spell];
    const label = effect?.displayName ?? spell;
    const getEffectWithoutTicks = (spellName: string, active: boolean) => {
      const hue = active ? red : green;
      const spellLookUp = convertSpellNameToUpper(spellName);
      const svg = resolveSVG(spellLookUp, hue);
      return svg;
    };
    const svg = getEffectWithoutTicks(spell, isToDo);
    return svg ? getSvgItemWithTooltip(svg, label) : null;
  };

  return (
    <SwipeableDrawer
      anchor='right'
      open={drawerOpen}
      onClose={toggleDrawer}
      onOpen={toggleDrawer}
      disableBackdropTransition={!iOS}
      disableDiscovery={iOS}
    >
      <Box sx={{ maxWidth: '100vw', p: 1, mb: 1 }}>
        <IconButton
          sx={{ width: 30, position: 'absolute' }}
          onClick={toggleDrawer}
          size='small'
        >
          <CloseIcon />
        </IconButton>
        <ProvinceView province={province} />

        {isEnemy && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}>
              <OpsSVG
                style={{
                  height: '30px',
                  width: '30px',
                  fill: `${theme.palette.primary.main}`,
                }}
              />
              <Typography variant='subtitle2' component={'div'}>
                War Plan Ops (<span style={{ color: green }}>ON</span>/
                <span style={{ color: red }}>OFF</span>):{' '}
              </Typography>
              {kanbanItem?.province.requiredOps
                ?.filter((o: string) => !province.requiredOps.includes(o))
                .map((opName: string) => {
                  return renderOp(
                    opName,
                    province.requiredOps.includes(opName)
                  );
                })}
              {kanbanItem?.province.requiredOps
                ?.filter((o: string) => province.requiredOps.includes(o))
                .map((opName: string) => {
                  return renderOp(
                    opName,
                    province.requiredOps.includes(opName)
                  );
                })}
              <Button
                variant='outlined'
                color='secondary'
                onClick={() => setOpsDrawerOpen(true)}
                size='small'
                sx={{ ml: 1 }}
              >
                Change
              </Button>
            </Box>

            <SwipeableDrawer
              anchor='right'
              open={opsDrawerOpen}
              onClose={toggleOpsDraw}
              onOpen={toggleOpsDraw}
              disableBackdropTransition={!iOS}
              disableDiscovery={iOS}
            >
              <ProvinceOps isWarPlan={isEnemy} />
            </SwipeableDrawer>
          </>
        )}

        <ProvinceGraphs province={province} />
      </Box>
    </SwipeableDrawer>
  );
};

export default DetailPanelContent;
